import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../auth/useApiClient';

const AnalyticsDashboard = () => {
  const { request } = useApiClient();
  const [days, setDays] = useState(30);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await request(`/api/analytics/summary?days=${days}&limit=${limit}`);
      if (!response.ok) {
        throw new Error('Unable to load analytics summary.');
      }
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setError('Could not load analytics summary. Please ensure backend is running with the latest code.');
      setSummary(null);
    }
    setLoading(false);
  }, [days, limit, request]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const totalQueryEvents = summary?.totals?.queryEvents || 0;
  const totalPathwayEvents = summary?.totals?.pathwayEvents || 0;
  const topQueries = summary?.topQueries || [];
  const topThemes = summary?.topPathwayThemes || [];
  const topLabels = summary?.topPathwayLabels || [];

  const windowLabel = useMemo(() => {
    if (!summary?.windowStart) return `${days}-day window`;
    const parsed = new Date(summary.windowStart);
    if (Number.isNaN(parsed.getTime())) return `${days}-day window`;
    return `${days}-day window from ${parsed.toLocaleDateString()}`;
  }, [summary, days]);

  return (
    <section className="page">
      <div className="page-header">
        <div className="section-label">Insights</div>
        <h1>Usage Analytics</h1>
        <p>Track most-asked questions and most-selected pathway patterns over time.</p>
      </div>

      <div className="card analytics-controls-card">
        <div className="analytics-controls-grid">
          <div>
            <label className="input-label">Time Window (days)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Number.parseInt(e.target.value, 10) || 30)}
            />
          </div>

          <div>
            <label className="input-label">Top Items Limit</label>
            <input
              type="number"
              min={1}
              max={50}
              value={limit}
              onChange={(e) => setLimit(Number.parseInt(e.target.value, 10) || 10)}
            />
          </div>

          <div className="analytics-controls-actions">
            <button className="btn-primary" onClick={loadSummary} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh Analytics'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="card analytics-message-card analytics-error">{error}</div>
      )}

      {!error && (
        <>
          <div className="analytics-meta-row">
            <span className="tag audience">{windowLabel}</span>
            <span className="tag keyword">Queries logged: {totalQueryEvents}</span>
            <span className="tag format">Pathway events logged: {totalPathwayEvents}</span>
          </div>

          <div className="analytics-grid">
            <div className="card analytics-list-card">
              <div className="section-label">Top Queries</div>
              {topQueries.length === 0 ? (
                <p className="analytics-empty">No query events available for this period.</p>
              ) : (
                <ul className="analytics-list">
                  {topQueries.map((item, index) => (
                    <li key={`query-${index}`}>
                      <span className="analytics-item-label">{item.query}</span>
                      <span className="analytics-item-count">{item.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card analytics-list-card">
              <div className="section-label">Top Pathway Themes</div>
              {topThemes.length === 0 ? (
                <p className="analytics-empty">No pathway theme usage available for this period.</p>
              ) : (
                <ul className="analytics-list">
                  {topThemes.map((item, index) => (
                    <li key={`theme-${index}`}>
                      <span className="analytics-item-label">{item.theme}</span>
                      <span className="analytics-item-count">{item.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="card analytics-list-card">
              <div className="section-label">Top Pathway Labels</div>
              {topLabels.length === 0 ? (
                <p className="analytics-empty">No pathway label usage available for this period.</p>
              ) : (
                <ul className="analytics-list">
                  {topLabels.map((item, index) => (
                    <li key={`label-${index}`}>
                      <span className="analytics-item-label">{item.label}</span>
                      <span className="analytics-item-count">{item.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default AnalyticsDashboard;
